import React, { useMemo } from 'react';
import { RefreshCw } from 'lucide-react';
import { usePreferencesStore } from '../../store/preferencesStore';
import { Toggle, Segmented, Row, Section } from '../settingsPrimitives';

/* ── Tab panels ──────────────────────────────────────────────────────────────── */
export const GeneralTab = ({ t, appVersion, desktopApp, checkingUpdates, checkFeedback, onCheckForUpdates }) => {
  const timeFormat           = usePreferencesStore((s) => s.timeFormat);
  const restoreLastSession   = usePreferencesStore((s) => s.restoreLastSession);
  const checkUpdatesFrequency = usePreferencesStore((s) => s.checkUpdatesFrequency);
  const language             = usePreferencesStore((s) => s.language);
  const setLanguage          = usePreferencesStore((s) => s.setLanguage);
  const update               = usePreferencesStore((s) => s.updatePreference);

  const languageOptions = useMemo(() => [
    { value: 'en', label: t('settings.languageEn') },
    { value: 'de', label: t('settings.languageDe') },
  ], [t]);

  const checkFeedbackClass =
    checkFeedback?.variant === 'success' ? 'text-green-400' :
    checkFeedback?.variant === 'error'   ? 'text-red-400'   :
    checkFeedback?.variant === 'warning' ? 'text-amber-400' :
                                           'text-blue-400';

  return (
    <>
      <Section title={t('settings.region')}>
        <Row label={t('settings.language')}>
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="settings-select-native"
          >
            {languageOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </Row>
        <Row label={t('settings.timeFormat')}>
          <Segmented
            value={timeFormat}
            onChange={(v) => update('timeFormat', v)}
            options={[
              { id: '12h', label: t('settings.twelveHour') },
              { id: '24h', label: t('settings.twentyFourHour') },
            ]}
          />
        </Row>
      </Section>

      <Section title={t('settings.startup')}>
        <Row label={t('settings.restoreLastSession')} desc={t('settings.restoreLastSessionDesc')}>
          <Toggle value={restoreLastSession} onChange={(v) => update('restoreLastSession', v)} />
        </Row>
        <Row label={t('settings.checkUpdatesFreq')} desc={t('settings.checkUpdatesFreqDesc')}>
          <select
            value={checkUpdatesFrequency}
            onChange={(e) => update('checkUpdatesFrequency', e.target.value)}
            className="settings-select-native"
          >
            <option value="launch">{t('settings.checkUpdatesOnLaunch')}</option>
            <option value="daily">{t('settings.checkUpdatesDaily')}</option>
            <option value="never">{t('settings.checkUpdatesNever')}</option>
          </select>
        </Row>
      </Section>

      <Section title={t('settings.app')}>
        <div className="settings-row">
          <div className="min-w-0">
            <div className="settings-row-label">{t('settings.currentVersion')}</div>
            <div className="font-mono text-xs mt-0.5" style={{ color: 'var(--accent, #8869eb)' }}>
              {appVersion || '…'}
            </div>
          </div>
          <div className="flex items-center flex-shrink-0 ml-4">
            {desktopApp ? (
              <button
                type="button"
                onClick={onCheckForUpdates}
                disabled={checkingUpdates}
                className="settings-action-btn settings-action-btn--tinted"
              >
                <RefreshCw size={13} className={checkingUpdates ? 'animate-spin' : ''} aria-hidden />
                {checkingUpdates ? t('settings.checkingForUpdates') : t('settings.checkForUpdates')}
              </button>
            ) : (
              <span className="text-xs" style={{ color: '#71717a' }}>
                {t('settings.updatesDesktopOnly')}
              </span>
            )}
          </div>
        </div>
        {checkFeedback && (
          <p className={`text-xs mt-2 ${checkFeedbackClass}`} role="status" aria-live="polite">
            {checkFeedback.message}
          </p>
        )}
      </Section>
    </>
  );
};
